import {
    IDefaultErrorState,
} from '@gaas/gaas-react-component-library';
import React, {useState} from 'react';
import TollAccountType from '../types/tollAccountType';
import TollProvider from '../types/TollProvider';

export const defaultErrorState: IDefaultErrorState = {
    errors: {},
    errorSummaries: {},
};

export enum TollReliefScheme {
    TR2 = 'TR2',
    TR3 = 'TR3',
}

export interface BusinessDetails {
    readonly abn: string;
    readonly businessName: string;
}

export enum AccountMetadata {
    ACTIVELY_PROCESSING_CLAIM_BLOCKS_UNLINKING = 'ACTIVELY_PROCESSING_CLAIM_BLOCKS_UNLINKING',
}

export interface LinkedAccount {
    readonly participantToken: string;
    readonly tollAccountType: TollAccountType;
    readonly tollAccountNumber: string;
    readonly tollProvider: TollProvider;
    readonly businessDetails?: BusinessDetails;
    readonly schemes: readonly TollReliefScheme[];
    readonly accountMetadata: AccountMetadata[]; // expect this would be empty for TR2

    /**
     * lpn and tollAccountEmail are required only for TR2 account for applying TR3
     * on the new TC page.
     */
    // TR2Only
    readonly lpn: string;
    readonly tollAccountEmail: string;
}

export interface TR3SchemeDetails {
    weeklyCap: number,
    tagRebateCap: number,
}

export interface Config {
    scheme: TollReliefScheme,
    financialYear: number,
    tollAccountType: TollAccountType,
    eligibilityPeriodDateFrom: string,
    eligibilityPeriodDateTo: string,
    claimPeriodFrom: string,
    claimPeriodTo: string,
    schemeDetails: TR3SchemeDetails,
}

export interface TermsConditions {
    agreeToTr2PersonalTerms?: boolean,
    agreeToTr2SoleTraderTerms?: boolean,
    agreeToTr3PersonalTerms?: boolean,
}

/**
 * A type to hold the custom toll relief data needed for submission.
 * To be progressively updated as user journeys through the linking process.
 */
export interface ProductData {
    /** Toll account user is trying to link. Populated by MTA page */
    readonly tollAccountType?: TollAccountType;
    /** Toll account details. Populated by SoleTraderTollAccountDetails page */
    readonly tollAccountDetailsPageData?: TollAccountDetailsData;
    /** Sole trader details - mixed/business only. Populated by SoleTraderDetails page */
    readonly soleTraderDetailData?: SoleTraderDetailData;

    readonly acceptNotifications?: boolean;

    readonly agreeTerms?: TermsConditions;
}

export interface TollAccountDetailsData {
    readonly lpn: string;
    readonly tollProvider: TollProvider;
    readonly tollAccountNumber: string;
    readonly tollAccountEmail: string;
}

export interface SoleTraderDetailData {
    readonly isRideShareOrTaxi?: boolean;
    readonly abn: string;
    readonly businessName: string;
}

export const useDefaultState = () => {
    const [linkedTollAccounts, setLinkedTollAccounts] = useState<LinkedAccount[]>([]);
    const [configs, setConfigs] = useState<Config[]>([]);

    /** Used when redirecting back to MTA page from a successful link, allowing success alert to show */
    const [successfullyLinkedAccountType, setSuccessfullyLinkedAccountType] = useState<TollAccountType | undefined>(undefined);

    /** eligibleTr2Account is TR2 account that is eligible for an upgrade to TR3 */
    const [eligibleTr2Account, setEligibleTr2Account] = useState<LinkedAccount | undefined>(undefined);

    /** For populating product data across pages to later be submitted */
    const [productData, setProductData] = useState<ProductData>({});

    return {
        configs,
        setConfigs,
        linkedTollAccounts,
        setLinkedTollAccounts,
        successfullyLinkedAccountType,
        setSuccessfullyLinkedAccountType,
        eligibleTr2Account,
        setEligibleTr2Account,
        productData,
        setProductData,
    };
}

export type TollRegistrationState = ReturnType<typeof useDefaultState>;

/**
 * A global state context for toll registration related stuff
 */
export const TollRegistrationContext = React.createContext({} as TollRegistrationState);

/**
 * A Provider to enable using a TollRegistrationContext.
 */
export const TollRegistrationProvider: React.FC = (props: React.PropsWithChildren<object>): React.JSX.Element => {
    const defaultState = useDefaultState();
    return <TollRegistrationContext.Provider value={defaultState}>
        {props.children}
    </TollRegistrationContext.Provider>;
};
